import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Send, Linkedin, Mail, Phone, ArrowUpRight, Loader2, Dribbble } from "lucide-react";
import { Reveal } from "./Reveal";
import { CONTACT } from "../lib/data";

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true, placeholder: "Your full name" },
  { name: "email", label: "Email", type: "email", required: true, placeholder: "you@company.com" },
  { name: "company", label: "Company", type: "text", required: false, placeholder: "Company name" },
  { name: "role", label: "Role", type: "text", required: false, placeholder: "e.g. Founder, Head of Product" },
  { name: "timeline", label: "Rough timeline", type: "text", required: false, placeholder: "e.g. Q2, 3 months, ASAP" },
];

const empty = { name: "", email: "", company: "", role: "", project_summary: "", timeline: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_summary) {
      toast.error("Please fill in your name, email, and project summary.");
      return;
    }
    setLoading(true);
    try {
      const templateParams = {
        from_name: form.name,
        reply_to: form.email,
        to_email: CONTACT.email,
        company: form.company,
        role: form.role,
        timeline: form.timeline,
        message: form.project_summary,
      };
      const res = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      if (res.status === 200) {
        toast.success("Thanks! Your project details are on their way. I'll respond within 24–48 hours.");
        setForm(empty);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error("Could not send your details. Please try again or email me directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-base/40 px-4 py-3.5 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow";

  return (
    <section id="contact" className="py-24 lg:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-6">Contact</p>
              <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]">
                Let&rsquo;s build <span className="text-gradient">something.</span>
              </h2>
              <p className="mt-6 text-lg text-ink-soft leading-relaxed font-light max-w-md">
                Need a designer-developer for your website, web app, or AI project? Share a few details and I&rsquo;ll respond within 24&ndash;48 hours.
              </p>

              <div className="mt-10 space-y-3">
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 glass rounded-2xl px-5 py-4 text-ink hover:text-brand transition-colors" data-testid="contact-email-link">
                  <Mail className="w-5 h-5 text-brand" /> {CONTACT.email}
                </a>
                <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-4 glass rounded-2xl px-5 py-4 text-ink hover:text-brand transition-colors" data-testid="contact-phone-link">
                  <Phone className="w-5 h-5 text-brand" /> {CONTACT.phone}
                </a>
                <div className="flex gap-3">
                  <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 glass rounded-2xl px-5 py-4 text-ink hover:text-brand transition-colors" data-testid="contact-linkedin-link">
                    <Linkedin className="w-5 h-5 text-brand" /> LinkedIn
                  </a>
                  <a href={CONTACT.behance} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 glass rounded-2xl px-5 py-4 text-ink hover:text-brand transition-colors" data-testid="contact-behance-link">
                    <Dribbble className="w-5 h-5 text-brand" /> Behance
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} noValidate className="glass rounded-3xl p-8 lg:p-10" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  {FIELDS.map((f) => (
                    <div key={f.name} className={f.name === "timeline" ? "sm:col-span-2" : ""}>
                      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-ink-soft mb-2">
                        {f.label} {f.required && <span className="text-brand">*</span>}
                      </label>
                      <input type={f.type} value={form[f.name]} onChange={(e) => update(f.name, e.target.value)} placeholder={f.placeholder} required={f.required} className={inputCls} data-testid={`contact-input-${f.name}`} />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-ink-soft mb-2">
                      Project summary <span className="text-brand">*</span>
                    </label>
                    <textarea value={form.project_summary} onChange={(e) => update("project_summary", e.target.value)} placeholder="Tell me about your project, its current status, and what you need help with." rows={5} required className={`${inputCls} resize-none`} data-testid="contact-input-project_summary" />
                  </div>
                </div>

                <button type="submit" disabled={loading} className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full btn-gradient px-8 py-4 text-base font-semibold shadow-xl shadow-brand/25 hover:opacity-90 transition-opacity disabled:opacity-60" data-testid="contact-submit">
                  {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending&hellip;</>) : (<>Send project details <Send className="w-4 h-4" /></>)}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
